import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserRound, CalendarDays, Megaphone, Images, Mail, ArrowRight, Wallet, TrendingUp, TrendingDown } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
import api from "@/lib/api";
import { formatDate } from "@/components/public/Shared";

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "Total Families", value: stats?.families, icon: Users, to: "/admin/families", testId: "stat-families" },
    { label: "Total Members", value: stats?.members, icon: UserRound, to: "/admin/members", testId: "stat-members" },
    { label: "Upcoming Events", value: stats?.upcoming_events, icon: CalendarDays, to: "/admin/events", testId: "stat-events" },
    { label: "Announcements", value: stats?.announcements, icon: Megaphone, to: "/admin/announcements", testId: "stat-announcements" },
    { label: "Gallery Photos", value: stats?.photos, icon: Images, to: "/admin/gallery", testId: "stat-photos" },
    { label: "Unread Messages", value: stats?.unread_messages, icon: Mail, to: "/admin/messages", testId: "stat-messages" },
  ];

  const financeCards = [
    { label: "Current Balance", value: stats ? inr(stats.ledger_balance) : "—", icon: Wallet, testId: "stat-balance" },
    { label: "Income (This Month)", value: stats ? inr(stats.ledger_month_income) : "—", icon: TrendingUp, testId: "stat-month-income" },
    { label: "Expenses (This Month)", value: stats ? inr(stats.ledger_month_expense) : "—", icon: TrendingDown, testId: "stat-month-expense" },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Welcome back. Here is an overview of the church records.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            data-testid={c.testId}
            className="group rounded-lg border border-border bg-white p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1"
          >
            <c.icon className="h-5 w-5 text-gold-dark" />
            <p className="mt-3 font-heading text-3xl font-bold text-navy">{stats ? (c.value ?? 0) : "—"}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="dash-finance-cards">
        {financeCards.map((c) => (
          <Link
            key={c.label}
            to="/admin/ledger"
            data-testid={c.testId}
            className="group rounded-lg border border-gold/40 bg-navy p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1"
          >
            <c.icon className="h-5 w-5 text-gold" />
            <p className="mt-3 font-heading text-2xl font-bold text-cream">{c.value}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-cream/60">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="min-w-0 rounded-lg border border-border bg-white p-6 shadow-sm" data-testid="dash-upcoming-events">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-navy">Next Events</h2>
            <Link to="/admin/events" className="flex items-center gap-1 text-xs font-semibold text-gold-dark hover:underline">
              Manage <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {(stats?.next_events || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming events. Add one from the Events section.</p>
          ) : (
            <ul className="divide-y divide-border">
              {stats.next_events.map((ev) => (
                <li key={ev.id} className="flex items-center justify-between gap-3 py-3">
                  <span className="min-w-0 flex-1 text-sm font-medium text-navy truncate">{ev.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{formatDate(ev.date)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="min-w-0 rounded-lg border border-border bg-white p-6 shadow-sm" data-testid="dash-recent-announcements">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-navy">Recent Announcements</h2>
            <Link to="/admin/announcements" className="flex items-center gap-1 text-xs font-semibold text-gold-dark hover:underline">
              Manage <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {(stats?.recent_announcements || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No announcements published yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {stats.recent_announcements.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 py-3">
                  <span className="min-w-0 flex-1 text-sm font-medium text-navy truncate">{a.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{formatDate(a.date)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="min-w-0 rounded-lg border border-border bg-white p-6 shadow-sm" data-testid="dash-recent-messages">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-navy">Latest Contact Messages</h2>
            <Link to="/admin/messages" className="flex items-center gap-1 text-xs font-semibold text-gold-dark hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {(stats?.recent_messages || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No messages received yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {stats.recent_messages.map((m) => (
                <li key={m.id} className="py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 flex-1 text-sm font-medium text-navy truncate">{m.name}</span>
                    {!m.read && <span className="h-2 w-2 shrink-0 rounded-full bg-gold" aria-label="Unread" />}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="min-w-0 rounded-lg border border-border bg-white p-6 shadow-sm" data-testid="dash-recent-activity">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-navy">Recent Activity</h2>
            <Link to="/admin/audit" className="flex items-center gap-1 text-xs font-semibold text-gold-dark hover:underline">
              Audit log <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {(stats?.recent_audit || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {stats.recent_audit.map((a) => (
                <li key={a.id} className="py-2.5">
                  <p className="text-sm text-navy">{a.detail || a.action}</p>
                  <p className="text-xs text-muted-foreground">{a.user_email} · {new Date(a.created_at).toLocaleString("en-IN")}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
